import { useState, useRef } from 'react';
import { UploadCloud02, X } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { AvatarProfilePhoto } from '@/components/base/avatar/avatar-profile-photo';
import { Select } from '@/components/base/select/select';
import { useMe } from '@/hooks/api/use-auth';
import { useUploadAvatar } from '@/hooks/api/use-profile';
import { getUserCompanies } from '@/data/api/companies';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

export const ProfilePictureUpload = () => {
    const { t } = useTranslation(['pages:profile', 'common']);
    const { data: currentUser } = useMe();
    const uploadAvatar = useUploadAvatar();
    const fileInputRef = useRef<HTMLInputElement>(null);
    
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [scope, setScope] = useState<'global' | string>('global');

    // Get user companies for company-specific avatars
    const { data: userCompanies = [] } = useQuery({
        queryKey: ['user-companies'],
        queryFn: getUserCompanies,
        staleTime: 5 * 60 * 1000,
    });

    const scopeOptions = [
        { id: 'global', label: t('picture.global') },
        ...userCompanies.map((company) => ({
            id: company.company_id,
            label: company.company_name,
        })),
    ];

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            // Validate file type
            const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                alert(t('picture.invalidFileType'));
                return;
            }

            // Validate file size (5MB)
            const maxSize = 5 * 1024 * 1024;
            if (file.size > maxSize) {
                alert(t('picture.fileTooLarge'));
                return;
            }

            setSelectedFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewUrl(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) return;

        await uploadAvatar.mutateAsync({
            file: selectedFile,
            company_id: scope === 'global' ? null : scope,
        });

        // Reset after successful upload
        setSelectedFile(null);
        setPreviewUrl(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleRemove = () => {
        setSelectedFile(null);
        setPreviewUrl(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const getInitials = () => {
        if (!currentUser?.full_name) return '';
        const parts = currentUser.full_name.split(' ');
        if (parts.length >= 2) {
            return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
        }
        return parts[0]?.[0]?.toUpperCase() || '';
    };

    // Determine avatar source: preview > user avatar > logo placeholder
    const getAvatarSrc = () => {
        if (previewUrl) return previewUrl;
        if (currentUser?.avatar_url) return currentUser.avatar_url;
        return '/logo-eve.svg'; // Placeholder logo when no avatar
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-6">
                <AvatarProfilePhoto
                    size="lg"
                    src={getAvatarSrc()}
                    initials={getInitials()}
                />
                <div className="flex-1 space-y-4">
                    <div>
                        <label className="text-sm font-medium text-primary mb-2 block">
                            {t('picture.scope')}
                        </label>
                        <Select
                            selectedKey={scope}
                            onSelectionChange={(key) => setScope(key as string)}
                            size="md"
                            className="max-w-xs"
                        >
                            {scopeOptions.map((option) => (
                                <Select.Item key={option.id} id={option.id}>
                                    {option.label}
                                </Select.Item>
                            ))}
                        </Select>
                        <p className="text-xs text-tertiary mt-2">
                            {t('picture.scopeDescription')}
                        </p>
                    </div>

                    <div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/jpg,image/webp"
                            onChange={handleFileSelect}
                            className="hidden"
                        />
                        <div className="flex items-center gap-3">
                            <Button
                                color="secondary"
                                size="md"
                                iconLeading={UploadCloud02}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                {t('picture.selectFile')}
                            </Button>
                            {selectedFile && (
                                <>
                                    <Button
                                        color="primary"
                                        size="md"
                                        onClick={handleUpload}
                                        isDisabled={uploadAvatar.isPending}
                                    >
                                        {uploadAvatar.isPending ? t('common:uploading') : t('picture.upload')}
                                    </Button>
                                    <Button
                                        color="tertiary"
                                        size="md"
                                        iconLeading={X}
                                        onClick={handleRemove}
                                    >
                                        {t('common:remove')}
                                    </Button>
                                </>
                            )}
                        </div>
                        {selectedFile && (
                            <p className="text-sm text-tertiary mt-2">
                                {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

